import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../config/environment'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: config.ADMIN_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookie-based authentication
})

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to admin login on 401 errors
      window.location.href = '/admin/login'
    }
    return Promise.reject(error)
  }
)

const creditService = {
  // Credit Balance APIs
  async createCreditBalance(data) {
    try {
      const response = await api.post('/organisation-credit-balances', data)
      toast.success('Credit balance created successfully')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create credit balance'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getCreditBalances(params = {}) {
    try {
      const response = await api.get('/organisation-credit-balances', { params })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch credit balances'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getCreditBalanceById(organisationId) {
    try {
      const response = await api.get(`/organisation-credit-balances/${organisationId}`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch credit balance'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  // Credit Transaction APIs
  async getCreditTransactions(organisationId, params = {}) {
    try {
      const response = await api.get(`/organisation-credit-balances/${organisationId}/transactions`, { params })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch credit transactions'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async getTransactionSummary(organisationId) {
    try {
      const response = await api.get(`/organisation-credit-balances/${organisationId}/summary`)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch transaction summary'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async addCreditTransaction(organisationId, data) {
    try {
      const response = await api.post(`/organisation-credit-balances/${organisationId}/credit`, data)
      toast.success('Credit transaction added successfully')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add credit transaction'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  async addDebitTransaction(organisationId, data) {
    try {
      const response = await api.post(`/organisation-credit-balances/${organisationId}/debit`, data)
      toast.success('Debit transaction added successfully')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add debit transaction'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  },

  // Organisation APIs (for dropdown)
  async getOrganisations() {
    try {
      const response = await api.get('/organisations')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch organisations'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  }
}

export { creditService }
